import { ColumnType } from './Store/Column';
import { FFactory } from './Formula/FormulaFact';
import { AggregateFunction } from './Formula/Funcs/AggFunc';
import { FFieldProvider } from './Formula/FormulaField';
import { FuncProvider } from './Formula/FuncProvider';
import { TypeProvider } from './Formula/TypeProvider';
import { DataType } from './DataType';
import { FieldType, RoleType } from './Field';
import { DataField } from './DataField';
import { SystemFieldName } from './SystemField';
import { ModelCalcField, ViewCalcField } from './CalcField';
import { ModelSystemField, ViewSystemField } from './SystemField';
import { Filter } from './Filter';
import { AlphaSort } from './Sort/AlphaSort';
import { DataSort } from './Sort/DataSort';
import { FieldSort } from './Sort/FieldSort';
import { PivotQuery } from './Engine/PivotQuery';
import { DomainQuery } from './Engine/DomainQuery';
import { FilterQuery } from './Engine/FilterQuery';
import { DatasetBuilder } from './Builders/DatasetBuilder';
import { ModelBuilder } from './Builders/ModelBuilder';
export class Model {
    static build(schema, data) {
        const builder = new DatasetBuilder();
        const dataset = builder.build(schema, data);
        return new ModelBuilder().build(dataset);
    }
    static convertModeSchema(schema) {
        return schema.map((c) => {
            let type = ColumnType.STRING;
            switch (c.type) {
                case 'boolean':
                    type = ColumnType.BOOLEAN;
                    break;
                case 'integer':
                case 'int':
                case 'bigint':
                    type = ColumnType.INTEGER;
                    break;
                case 'string':
                case 'character':
                case 'character varying':
                case 'varchar':
                case 'varchar2':
                case 'nvarchar2':
                case 'text':
                case 'json':
                    type = ColumnType.STRING;
                    break;
                case 'float':
                case 'decimal':
                case 'double precision':
                case 'double':
                case 'real':
                case 'fixed':
                case 'number':
                    type = ColumnType.FLOAT;
                    break;
                case 'datetime':
                case 'date':
                case 'time':
                case 'timestamp with time zone':
                case 'timestamp':
                case 'timestamp without time zone':
                    type = ColumnType.DATETIME;
                    break;
            }
            return { name: c.name, type };
        });
    }
    constructor(engine, fields) {
        this._engine = engine;
        this._fields = new Array();
        this._defaultFieldProps = new Map();
        this._formulas = new FFactory(new FFieldProvider(this), new FuncProvider(), new TypeProvider());
        this._names = new ModelSystemField(SystemFieldName.NAMES);
        this._values = new ModelSystemField(SystemFieldName.VALUES);
        this._multiple = new ModelSystemField(SystemFieldName.MULTIPLE);
        (fields || []).forEach((field) => {
            this.addField(field);
        });
    }
    get engine() {
        return this._engine;
    }
    get fields() {
        return this._fields;
    }
    get defaultFieldProps() {
        return this._defaultFieldProps;
    }
    get namesField() {
        return this._names;
    }
    get valuesField() {
        return this._values;
    }
    get multipleField() {
        return this._multiple;
    }
    get formulas() {
        return this._formulas;
    }
    addField(field) {
        this.fields.push(field);
        switch (field.type) {
            case FieldType.DATA:
                switch (field.roleType) {
                    case RoleType.DIMENSION:
                        this.defaultFieldProps.set(field.name, {
                            roleType: RoleType.DIMENSION,
                        });
                        break;
                    case RoleType.MEASURE:
                        this.defaultFieldProps.set(field.name, {
                            aggregate: AggregateFunction.SUM,
                            roleType: RoleType.MEASURE,
                        });
                        break;
                }
                break;
            case FieldType.MODELCALC:
                switch (field.roleType) {
                    case RoleType.DIMENSION:
                        this.defaultFieldProps.set(field.formula.source, {
                            roleType: RoleType.DIMENSION,
                        });
                        break;
                    case RoleType.MEASURE:
                        this.defaultFieldProps.set(field.formula.source, {
                            aggregate: AggregateFunction.SUM,
                            roleType: RoleType.MEASURE,
                        });
                        break;
                }
                break;
        }
    }
    getField(name) {
        switch (name) {
            case SystemFieldName.VALUES:
                return this.valuesField;
            case SystemFieldName.NAMES:
                return this.namesField;
            default:
                return this.fields.find((f) => f.name === name);
        }
    }
    getDefaultFieldProps(name) {
        return this.defaultFieldProps.get(name);
    }
    getDimensions() {
        return this.fields.filter((f) => f.isDimension);
    }
    getMeasures() {
        return this.fields.filter((f) => f.isMeasure);
    }
    compile(source) {
        return this.formulas.compile(source);
    }
    validate(source) {
        return this.formulas.validate(source);
    }
    onPivot(callback) {
        const query = new PivotQuery();
        if (callback != null) {
            callback(query);
        }
        return this.engine.pivot(this, query);
    }
    onFilter(callback) {
        const query = new FilterQuery();
        if (callback != null) {
            callback(query);
        }
        return this.engine.filter(this, query);
    }
    onDomain(callback) {
        const query = new DomainQuery();
        if (callback != null) {
            callback(query);
        }
        return this.engine.domain(this, query);
    }
    /**
     * Helpers
     */
    /**
     * TODO: Move this to a sheet / renderer location
     */
    getDefaultViewFieldName(field, roleType) {
        switch (field.type) {
            case FieldType.VIEWNAMES:
            case FieldType.VIEWVALUES:
            case FieldType.MODELNAMES:
            case FieldType.MODELVALUES:
                return field.name;
            case FieldType.VIEWCALC:
                return field.formula.source;
        }
        switch (roleType) {
            case RoleType.DIMENSION:
                return `[${field.name}]`;
            case RoleType.MEASURE:
                switch (field.type) {
                    case FieldType.DATA:
                    case FieldType.MODELCALC: {
                        const defaultProps = this.getDefaultFieldProps(field.name);
                        if (defaultProps == null) {
                            return `${AggregateFunction.SUM}([${field.name}])`;
                        }
                        switch (defaultProps.roleType) {
                            case RoleType.DIMENSION:
                                switch (field.dataType) {
                                    case DataType.BOOLEAN:
                                    case DataType.DATE:
                                    case DataType.STRING:
                                        return `${AggregateFunction.COUNT}([${field.name}])`;
                                    default:
                                        return `${AggregateFunction.SUM}([${field.name}])`;
                                }
                            case RoleType.MEASURE:
                                return `${defaultProps.aggregate}[${field.name}]`;
                        }
                    }
                }
        }
    }
    getDefaultFieldName(field) {
        switch (field.type) {
            case FieldType.VIEWNAMES:
                return 'Measure Names';
            case FieldType.VIEWVALUES:
                return 'Measure Values';
            case FieldType.VIEWCALC:
                switch (field.roleType) {
                    case RoleType.DIMENSION:
                        return field.name;
                    case RoleType.MEASURE:
                        return this.getDefaultMeasureName(field);
                }
        }
    }
    getDefaultMeasureName(field) {
        if (field.type === FieldType.VIEWCALC && field.formula.form === 'AGGFUNC') {
            const modelField = this.getField(field.formula.fieldName);
            let defaultProps;
            switch (modelField.type) {
                case FieldType.DATA:
                    defaultProps = this.getDefaultFieldProps(field.formula.fieldName);
                    break;
                case FieldType.MODELCALC:
                    defaultProps = this.getDefaultFieldProps(modelField.formula.source);
                    break;
            }
            let defaultAggFunc = AggregateFunction.SUM;
            if (defaultProps != null && defaultProps.roleType === RoleType.MEASURE) {
                defaultAggFunc = defaultProps.aggregate;
            }
            if (field.formula.funcName === defaultAggFunc) {
                return modelField.name;
            }
            switch (field.formula.funcName) {
                case AggregateFunction.AVG:
                    return `Avg of ${modelField.name}`;
                case AggregateFunction.COUNT:
                    return `Count of ${modelField.name}`;
                case AggregateFunction.COUNTD:
                    return `Distinct Count of ${modelField.name}`;
                case AggregateFunction.MEDIAN:
                    return `Median of ${modelField.name}`;
                case AggregateFunction.MIN:
                    return `Min of ${modelField.name}`;
                case AggregateFunction.MAX:
                    return `Max of ${modelField.name}`;
                case AggregateFunction.STDEV:
                    return `Std. Dev. of ${modelField.name}`;
                case AggregateFunction.STDEVP:
                    return `Pop. Std. Dev. of ${modelField.name}`;
                case AggregateFunction.SUM:
                    return `Sum of ${modelField.name}`;
                case AggregateFunction.VAR:
                    return `Variance of ${modelField.name}`;
                case AggregateFunction.VARP:
                    return `Pop. Variance of ${modelField.name}`;
                default:
                    return field.name;
            }
        }
        else {
            return field.name;
        }
    }
    /**
     * Fields
     */
    buildDataField(columnName, dataType, roleType, varType, name) {
        const column = this.engine.dataset.schema.getColumn(columnName);
        return new DataField(column, dataType, roleType, varType, name);
    }
    buildCalcField(name, source, dataType, varType) {
        return new ModelCalcField(name, this.compile(source), dataType, varType);
    }
    buildViewCalcField(source, varType, id, sid) {
        return new ViewCalcField(this.compile(source), varType, id, sid);
    }
    buildViewSystemField(name, id, sid) {
        return new ViewSystemField(name, id, sid);
    }
    buildViewNamesField(id, sid) {
        return this.buildViewSystemField(SystemFieldName.NAMES, id, sid);
    }
    buildViewValuesField(id, sid) {
        return this.buildViewSystemField(SystemFieldName.VALUES, id, sid);
    }
    /**
     * Sorting
     */
    buildDataSort(field, order) {
        return new DataSort(field, order);
    }
    buildAlphaSort(field, order) {
        return new AlphaSort(field, order);
    }
    buildFieldSort(field, byField, order) {
        return new FieldSort(field, byField, [], order);
    }
    buildFilteredFieldSort(field, byField, byFilters, order) {
        return new FieldSort(field, byField, byFilters, order);
    }
    /**
     * Filtering
     */
    buildContinuousFilter(field, condition) {
        return Filter.makeContinuousFilter(field, condition);
    }
    buildDiscreteFilter(field, condition) {
        return Filter.makeDiscreteFilter(field, condition);
    }
    buildFilter(field, condition) {
        return Filter.makeFilter(field, condition);
    }
}
